import React from 'react';
import PropTypes from 'prop-types';
import RewardItems from './RewardItems';
import GLOBALS from '../config/globals';
import SpecialGameReward from './SpecialGameReward';
import BadgeReward from './BadgeReward';

const PerkDetails = ({ perkDetails }) => {
    if (!perkDetails || perkDetails.length === 0) return null;

    const superStars = perkDetails.filter(p => p.perk === 'superStar');
    const drugDealers = perkDetails.filter(p => p.perk === 'drugDealer');
    const drugDealerHits = drugDealers.filter(p => p.result.includes('+1'));

    return (
        <div className="postgame-perk-details">
            {superStars.length > 0 && (
                <div className="postgame-perk-section">
                    <div className="postgame-perk-header">Super Star</div>
                    {superStars.map((p, i) => (
                        <div key={`star-${i}`} className="postgame-perk-row">
                            <span className="postgame-perk-flavor">{p.result}</span>
                        </div>
                    ))}
                </div>
            )}
            {drugDealers.length > 0 && (
                <div className="postgame-perk-section">
                    <div className="postgame-perk-header">
                        Drug Dealer {drugDealerHits.length > 0 ? `(${drugDealerHits.length} scored)` : '(none scored)'}
                    </div>
                    {drugDealers.map((p, i) => (
                        <div key={`dealer-${i}`} className={`postgame-perk-row ${p.result.includes('+1') ? '' : 'postgame-perk-miss'}`}>
                            <span className="postgame-perk-flavor">{p.result}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const PostGameRewardsModal = ({ rewardsAwarded, didUserWin, finalScore, onClose }) => {
    if (!rewardsAwarded) return null;

    const { game: gameReward, campaign } = rewardsAwarded;

    return (
        <div className="postgame-rewards-modal">
            <div className="postgame-rewards-top">
                <div className="postgame-rewards-top-left">
                    <div className={`postgame-rewards-header ${didUserWin ? 'win' : 'loss'}`}>
                        {didUserWin ? 'Victory!' : 'Defeat'}
                    </div>
                    <div className="postgame-rewards-score">
                        Final: {finalScore.home} - {finalScore.away}
                    </div>
                </div>
                {(gameReward || rewardsAwarded.specialGame) && (
                    <div className="postgame-rewards-top-right">
                        {gameReward && <RewardItems reward={gameReward} hidePerkDetails />}
                        <SpecialGameReward specialGame={rewardsAwarded.specialGame} />
                    </div>
                )}
            </div>

            {gameReward?.perkDetails && gameReward.perkDetails.length > 0 && (
                <PerkDetails perkDetails={gameReward.perkDetails} />
            )}

            {campaign && (
                <div className={`postgame-rewards-campaign ${campaign.result === GLOBALS.RESULT.WIN ? 'campaign-win' : 'campaign-loss'}`}>
                    <div className="postgame-rewards-campaign-header">
                        {campaign.result === GLOBALS.RESULT.WIN ? 'Campaign Complete!' : 'Series Lost'}
                    </div>
                    <div className="postgame-rewards-campaign-record">
                        {campaign.contestName} — {GLOBALS.RESULT.LABEL[campaign.result] || campaign.result} {campaign.wins}-{campaign.losses}
                    </div>
                    {campaign.result === GLOBALS.RESULT.WIN && campaign.reward && (
                        <RewardItems reward={campaign.reward} hidePerkDetails />
                    )}
                </div>
            )}

            {rewardsAwarded.playoff && (
                <div className={`postgame-rewards-playoff ${didUserWin ? 'playoff-advance' : 'playoff-eliminated'}`}>
                    <div className="postgame-rewards-playoff-header">
                        {didUserWin ? `${rewardsAwarded.playoff.roundName} Won!` : `Eliminated — ${rewardsAwarded.playoff.roundName}`}
                    </div>
                    {didUserWin && rewardsAwarded.playoff.reward && (
                        <div className="postgame-rewards-playoff-packs">
                            {rewardsAwarded.playoff.reward.map((pack, i) => (
                                <span key={i} className="postgame-playoff-pack">
                                    {pack.amount}x {pack.seriesKey.replace('tournament-', '').replace(/^\w/, c => c.toUpperCase())} Pack
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {rewardsAwarded.badges && rewardsAwarded.badges.length > 0 ? (
                <div className="postgame-badges">
                    {rewardsAwarded.badges.map(badge => (
                        <BadgeReward key={badge.badgeID} badge={badge} />
                    ))}
                </div>
            ) : null}

            <button className="button postgame-rewards-continue" onClick={onClose}>
                Continue
            </button>
        </div>
    );
};

PostGameRewardsModal.propTypes = {
    rewardsAwarded: PropTypes.shape({
        game: PropTypes.object,
        campaign: PropTypes.object,
        specialGame: PropTypes.object,
        playoff: PropTypes.object,
        badges: PropTypes.arrayOf(PropTypes.shape({
            badgeID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
            name: PropTypes.string.isRequired,
            description: PropTypes.string,
            groupKey: PropTypes.string,
            tier: PropTypes.number,
            imageKey: PropTypes.string,
            playerName: PropTypes.string,
            cardID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        })),
    }),
    didUserWin: PropTypes.bool.isRequired,
    finalScore: PropTypes.shape({
        home: PropTypes.number,
        away: PropTypes.number,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
};

export default PostGameRewardsModal;
